import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { LoaderCircle } from 'lucide-react';
import { cn } from './cn.js';
import { Tooltip } from './tooltip.js';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> {
  /// O que o botão faz, com o contexto necessário. Vira o nome acessível E a dica visível — não há
  /// como criar um destes sem rótulo, que é a armadilha clássica do botão só de ícone.
  label: string;
  icon: ReactNode;
  tone?: 'neutral' | 'danger';
  loading?: boolean;
}

/// Ação de linha de tabela: só o ícone, com a dica dizendo o que ele faz.
///
/// Com uma dúzia de linhas na tela, botão com texto vira uma parede de retângulos que disputa
/// atenção com o dado — que é o que a pessoa veio ler. O ícone fica discreto em repouso e ganha
/// cor quando o ponteiro chega ou o teclado foca.
export function IconButton({
  label,
  icon,
  tone = 'neutral',
  className,
  loading = false,
  disabled,
  onClick,
  ...props
}: IconButtonProps) {
  const button = (
    <button
      type="button"
      data-slot="icon-button"
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      className={cn(
        'grid size-9 shrink-0 cursor-pointer place-items-center rounded-lg text-content-faint transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'danger'
          ? 'enabled:hover:bg-danger/12 enabled:hover:text-danger'
          : 'enabled:hover:bg-content/5 enabled:hover:text-content',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className="grid size-4 place-items-center [&_svg]:size-4 [&_svg]:shrink-0"
      >
        {loading ? <LoaderCircle className="animate-spin" /> : icon}
      </span>
    </button>
  );
  return (
    <Tooltip content={label}>
      <span
        role={disabled || loading ? 'group' : undefined}
        aria-label={disabled || loading ? label : undefined}
        tabIndex={disabled || loading ? 0 : undefined}
        className="inline-flex shrink-0"
        onClick={(event) => event.stopPropagation()}
      >
        {button}
      </span>
    </Tooltip>
  );
}
